import { ReactElement } from "react";
import { Paper, Typography, Button, Box } from "@mui/material";
import {
  Add as AddIcon,
  Science as ScienceIcon,
  Rocket as RocketIcon,
  TrendingUp as TrendingUpIcon,
} from "@mui/icons-material";

export interface BasePaperProps {
  className?: string;
  hasProjects?: boolean;
  onNewProject?: () => void;
  showActionButtons?: boolean;
  onBack?: () => void;
  onContinue?: () => void;
  children?: React.ReactNode;
}

const BasePaper = ({
  className = "",
  hasProjects = true,
  onNewProject,
  showActionButtons = false,
  onBack,
  onContinue,
  children,
}: BasePaperProps): ReactElement => {
  if (!hasProjects) {
    return (
      <Paper
        elevation={1}
        className={`flex-1 p-8 flex flex-col items-center justify-center text-center ${className}`}
        sx={{
          backgroundColor: "#fafafa",
          borderRadius: "12px",
          border: "1px solid #e0e0e0",
          height: "100%",
          minHeight: "100%",
          overflow: "auto",
        }}
      >
        <ScienceIcon sx={{ fontSize: 80, color: "#19C1DB", mb: 3 }} />
        <Typography
          variant="h3"
          className="text-gray-900 mb-4"
          sx={{ fontWeight: 600 }}
        >
          Welcome to SurvOm!
        </Typography>
        <Typography
          variant="body1"
          className="text-gray-600 mb-8"
          sx={{ fontSize: "1.1rem", lineHeight: 1.6 }}
        >
          Start your analysis journey by creating your first project. Unlock the
          power of multi-omics data analysis and discover insights that drive
          precision medicine forward.
        </Typography>

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={onNewProject}
          className="bg-[#19C1DB] hover:bg-[#17B1CB] text-white font-semibold py-3 px-8"
          sx={{
            textTransform: "none",
            fontSize: "1.1rem",
            borderRadius: "8px",
          }}
        >
          Create Your First Project
        </Button>

        <div className="flex flex-wrap justify-center gap-6 mt-8 text-sm text-gray-500">
          <div className="flex items-center gap-2">
            <RocketIcon sx={{ fontSize: 20 }} />
            <Typography variant="body2" className="text-gray-500">
              Launch your research
            </Typography>
          </div>
          <div className="flex items-center gap-2">
            <TrendingUpIcon sx={{ fontSize: 20 }} />
            <Typography variant="body2" className="text-gray-500">
              Discover patterns
            </Typography>
          </div>
          <div className="flex items-center gap-2">
            <ScienceIcon sx={{ fontSize: 20 }} />
            <Typography variant="body2" className="text-gray-500">
              Advance medicine
            </Typography>
          </div>
        </div>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={1}
      className={`flex-1 p-8 flex flex-col ${showActionButtons ? "justify-between" : "items-center justify-center text-center"} ${className}`}
      sx={{
        backgroundColor: "#fafafa",
        borderRadius: "12px",
        border: "1px solid #e0e0e0",
        height: "100%",
        minHeight: "100%",
        overflow: "auto",
      }}
    >
      {children ? (
        <>
          <Box flex={1}>{children}</Box>
          {showActionButtons && (
            <Box
              display="flex"
              justifyContent="space-between"
              gap={3}
              mt={4}
              pt={3}
              borderTop="1px solid #e5e7eb"
            >
              <Button
                variant="outlined"
                onClick={onBack}
                sx={{
                  textTransform: "none",
                  fontWeight: 500,
                  px: 3,
                  py: 1.5,
                }}
              >
                Back
              </Button>
              <Button
                variant="contained"
                onClick={onContinue}
                sx={{
                  textTransform: "none",
                  fontWeight: 500,
                  px: 3,
                  py: 1.5,
                  backgroundColor: "#19C1DB",
                  "&:hover": {
                    backgroundColor: "#17B1CB",
                  },
                }}
              >
                Continue
              </Button>
            </Box>
          )}
        </>
      ) : (
        <>
          <Typography variant="h4" className="text-gray-500 mb-4">
            Select a project to get started
          </Typography>
          <Typography variant="body1" className="text-gray-400">
            Choose a project from the sidebar to view its details and analysis
          </Typography>
        </>
      )}
    </Paper>
  );
};

export default BasePaper;
